import { isReorderItemId, type ReorderIntent } from "./oneClickReorderParams";

export const REORDER_INTENT_STORAGE_KEY = "sgReorderIntent";

/**
 * A reorder link is redeemed against whoever is signed in when it resumes, so a stale
 * intent left in a shared tab must not survive long enough for the next person to sign
 * in and trigger it. Ten minutes covers a login round trip with room to spare.
 */
export const REORDER_INTENT_MAX_AGE_MS = 10 * 60 * 1000;

interface StoredReorderIntent extends ReorderIntent {
  /** Epoch ms at write time. An intent with no usable stamp cannot be proven fresh. */
  createdAt: number | null;
  /** The sg_customer_id observed at write time, or null when nobody was signed in. */
  identity: string | null;
  /** Set when the logged-out branch sends the visitor to Auth0, so a second pass can give up. */
  bounced: boolean;
}

/**
 * The reorder intent has to survive the Auth0 login bounce, so it is parked in
 * sessionStorage. Every access is guarded: sessionStorage throws outright in
 * private-mode Safari and when a browser blocks site data, and a reorder link
 * must never take the page down with it.
 */
function readRaw(): string | null {
  try {
    return sessionStorage.getItem(REORDER_INTENT_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** @returns false when the browser refused the write — private mode, blocked site data, quota. */
function writeRaw(value: string): boolean {
  try {
    sessionStorage.setItem(REORDER_INTENT_STORAGE_KEY, value);
    return true;
  } catch {
    // A reorder that can't be persisted still works in the same tab; don't break the page.
    return false;
  }
}

function readString(value: object, key: string): string | null {
  const field = Reflect.get(value, key);
  return typeof field === "string" ? field : null;
}

function readCreatedAt(value: object): number | null {
  const createdAt = Reflect.get(value, "createdAt");
  if (typeof createdAt !== "number" || !Number.isFinite(createdAt)) return null;
  return createdAt;
}

function isFresh(createdAt: number | null): boolean {
  if (createdAt === null) return false;
  const age = Date.now() - createdAt;
  return age >= 0 && age < REORDER_INTENT_MAX_AGE_MS;
}

/** Two ids are the same account regardless of case — Java emits lowercase, Auth0 may not. */
function isSameIdentity(stored: string | null, current: string | null): boolean {
  if (!stored || !current) return true;
  return stored.toLowerCase() === current.toLowerCase();
}

function parseStored(raw: string): StoredReorderIntent | null {
  const parsed: unknown = JSON.parse(raw);
  if (typeof parsed !== "object" || parsed === null) return null;

  const originalItemId = readString(parsed, "originalItemId");
  if (!isReorderItemId(originalItemId)) return null;

  return {
    originalItemId,
    couponCode: readString(parsed, "couponCode"),
    createdAt: readCreatedAt(parsed),
    identity: readString(parsed, "identity"),
    bounced: Reflect.get(parsed, "bounced") === true,
  };
}

function readStored(): StoredReorderIntent | null {
  const raw = readRaw();
  if (!raw) return null;

  try {
    return parseStored(raw);
  } catch {
    return null;
  }
}

/**
 * Non-throwing, but never silent: the caller strips the params off the URL immediately after
 * this, so a failed write leaves no copy of the intent anywhere.
 *
 * A refused write is not an empty store. Quota-exceeded is the realistic shape — setItem throws
 * while everything already written stays readable, including THIS key's previous record. Left
 * alone it goes on answering for the link the visitor just clicked: its item id to getReorderIntent,
 * its `bounced` to hasBouncedReorderIntent — and the reset to false that a successful write performs
 * is exactly the write that failed, so a brand new link inherits an older one's trip to login.
 * So drop the key and take one more pass. Dropping it is also the only quota this function can
 * free, so a refusal our own entry caused usually becomes a successful write; and when it does
 * not, the stale record is gone either way and can shadow nothing.
 *
 * @returns whether the intent actually reached sessionStorage.
 */
export function setReorderIntent(intent: ReorderIntent, identity: string | null = null): boolean {
  const stored: StoredReorderIntent = { ...intent, createdAt: Date.now(), identity, bounced: false };
  const raw = JSON.stringify(stored);
  if (writeRaw(raw)) return true;

  clearReorderIntent();

  return writeRaw(raw);
}

/**
 * Hands back the stored intent only when it is still redeemable: a well-formed item id,
 * written within the last ten minutes, and not stamped with a different account than the
 * one signed in now. Anything else is null — the flow then treats it as no intent at all.
 */
export function getReorderIntent(currentIdentity: string | null = null): ReorderIntent | null {
  const stored = readStored();
  if (!stored || !isFresh(stored.createdAt)) return null;
  if (!isSameIdentity(stored.identity, currentIdentity)) return null;

  return { originalItemId: stored.originalItemId, couponCode: stored.couponCode };
}

/**
 * Records that this intent has already sent the visitor to Auth0 once.
 *
 * Deliberately no remove-then-retry here, unlike setReorderIntent: this key holds the intent the
 * visitor is about to be sent away from, and it is the only copy that survives the trip. Dropping
 * it to make room would lose the reorder outright. A refused mark just leaves the flag unset —
 * worth one extra bounce, not worth the intent.
 */
export function markReorderIntentBounced(): void {
  const stored = readStored();
  if (!stored) return;

  writeRaw(JSON.stringify({ ...stored, bounced: true }));
}

export function hasBouncedReorderIntent(): boolean {
  return readStored()?.bounced === true;
}

export function clearReorderIntent(): void {
  try {
    sessionStorage.removeItem(REORDER_INTENT_STORAGE_KEY);
  } catch {
    // Nothing to recover from — the intent is already unusable.
  }
}
